import { isDevMode, WritableSignal } from '@angular/core';

import { take } from 'rxjs';

import type { UrlModalParams, UrlModalRequest } from '@kros-sk/router-stores/module-router-store/query';
import { type KrosModalCloseEvent, type KrosModalRef } from '@kros-sk/ui/modal';

import { UrlModalDeclaration } from './url-modal.types';

/**
 * How a modal that could not open ends: as a close of its own whose param leaves the URL without a history entry, as a
 * modal that did its job does. The URL then names no modal that is not open, and a later click or link opens it afresh.
 */
const ENDED_UNOPENED: KrosModalCloseEvent = { type: 'submit', data: null };

/** What an opened modal needs from the engine. */
export interface OpenedUrlModalHooks {
  /** The modal request the URL holds right now. */
  readonly currentRequest: () => UrlModalRequest | null;
  /** The router's URL right now, its path and query: what a modal that starts to close closes under. */
  readonly currentUrl: () => string;
  /** Runs once the opener has opened the window, before the modal listens to its close. */
  readonly opened: (ref: KrosModalRef) => void;
  /** Runs at `afterClosed$`, before anything waiting on `closed` continues. */
  readonly afterClosed: (modal: OpenedUrlModal, event: KrosModalCloseEvent) => void;
}

/**
 * One modal the engine opened, from the moment it opens until its `afterClosed$`.
 *
 * A declaration that prepares its modal keeps it waiting first, without a window (672307). It is the engine's modal all
 * the same: whatever would close it calls the preparation off, and a preparation that fails ends it as a close of its
 * own that takes its param out of the URL. An opener that throws ends it the same way, and `shown` rejects with the
 * error.
 *
 * `KrosModalRef` reports a close twice: `beforeClose$` at once, `afterClosed$` 50 ms later. In between the modal is
 * closing. It is on its way out, but it is still the engine's modal, so the next one waits for `closed`.
 */
export class OpenedUrlModal {
  /** The URL holds the modal: it is `always`, or `fromLink` and a link has named it. */
  isInUrl: boolean;

  /** Resolves at `afterClosed$`. */
  readonly closed: Promise<void>;

  /**
   * Resolves `true` once the window has opened, or `false` once the modal ended without one. Rejects with the opener's
   * error when the opener throws.
   */
  readonly shown: Promise<boolean>;

  #phase: 'preparing' | 'open' | 'closing' = 'preparing';
  #ref: KrosModalRef | null = null;
  #closedByEngine = false;
  #requestAtClose: UrlModalRequest | null = null;
  #urlAtClose: string | null = null;
  readonly #preparation = new AbortController();
  readonly #hooks: OpenedUrlModalHooks;
  #markClosed!: () => void;
  #settleShown!: { resolve: (isShown: boolean) => void; reject: (failure: unknown) => void };

  constructor(
    readonly modalId: string,
    readonly declaration: UrlModalDeclaration,
    readonly params: WritableSignal<UrlModalParams>,
    isInUrl: boolean,
    hooks: OpenedUrlModalHooks
  ) {
    this.isInUrl = isInUrl;
    this.#hooks = hooks;
    this.closed = new Promise<void>((resolve) => (this.#markClosed = resolve));
    this.shown = new Promise<boolean>((resolve, reject) => (this.#settleShown = { resolve, reject }));
  }

  /** Before the opener has run: the declaration's `prepare` is under way. */
  get isPreparing(): boolean {
    return this.#phase === 'preparing';
  }

  /** Between `beforeClose$` and `afterClosed$`. */
  get isClosing(): boolean {
    return this.#phase === 'closing';
  }

  /** The engine closed it (a swap, a URL that no longer names it, its owner going away), not the modal itself. */
  get closedByEngine(): boolean {
    return this.#closedByEngine;
  }

  /**
   * The URL's request when the modal began to close, if it named this modal. On a Back the router has not moved yet at
   * that moment, so this is the request the modal was open under.
   */
  get requestAtClose(): UrlModalRequest | null {
    return this.#requestAtClose;
  }

  /**
   * The router's URL, path and query, when the modal began to close. On a Back the router has not moved yet at that
   * moment, so this is the URL the modal was open at.
   */
  get urlAtClose(): string | null {
    return this.#urlAtClose;
  }

  /** Runs the declaration's `prepare`, and `open` once it resolves. Without a `prepare`, `open` runs at once. */
  start(open: () => KrosModalRef): void {
    const prepare = this.declaration.prepare;

    if (!prepare) {
      this.#open(open);

      return;
    }

    void new Promise<void>((resolve) => resolve(prepare(this.#preparation.signal))).then(
      () => {
        if (this.isPreparing) {
          this.#open(open);
        }
      },
      (failure: unknown) => {
        if (this.isPreparing) {
          reportUnprepared(this.modalId, failure);
          this.#endWithoutWindow(ENDED_UNOPENED);
          this.#settleShown.resolve(false);
        }
      }
    );
  }

  /** Closes it on the engine's behalf, or calls its preparation off. Does nothing once it is closing. */
  cancelByEngine(): void {
    if (this.isClosing) {
      return;
    }

    this.#closedByEngine = true;

    if (this.#ref) {
      this.#ref.cancel();

      return;
    }

    this.#preparation.abort();
    this.#endWithoutWindow({ type: 'cancel', data: null });
    this.#settleShown.resolve(false);
  }

  #open(open: () => KrosModalRef): void {
    let ref: KrosModalRef;

    try {
      ref = open();
    } catch (failure: unknown) {
      this.#endWithoutWindow(ENDED_UNOPENED);
      this.#settleShown.reject(failure);

      return;
    }

    this.#ref = ref;
    this.#phase = 'open';
    this.#hooks.opened(ref);
    ref.beforeClose$.pipe(take(1)).subscribe(() => this.#beginClosing());
    ref.afterClosed$.pipe(take(1)).subscribe((event: KrosModalCloseEvent) => this.#close(event));
    this.#settleShown.resolve(true);
  }

  #beginClosing(): void {
    const request = this.#hooks.currentRequest();

    this.#phase = 'closing';
    this.#requestAtClose = request?.modalId === this.modalId ? request : null;
    this.#urlAtClose = this.#hooks.currentUrl();
  }

  #close(event: KrosModalCloseEvent): void {
    this.#hooks.afterClosed(this, event);
    this.#markClosed();
  }

  /** A modal that never got its window closes as one that had it does, both moments at once. */
  #endWithoutWindow(event: KrosModalCloseEvent): void {
    this.#beginClosing();
    this.#close(event);
  }
}

function reportUnprepared(modalId: string, failure: unknown): void {
  if (isDevMode()) {
    console.warn(`The URL modal "${modalId}" could not be prepared, so it does not open.`, failure);
  }
}
